/**
 * Holding a plan to what the same event spent the last times it ran.
 *
 * The server finds the runs (`GET /promotions/last-run`, and `last_run` on each Accrual row:
 * same account, same type, same name once months/waves/years are dropped, median of up to three
 * settled runs). This file decides when the plan is far enough off, either way, to say so — the forms and the
 * Accrual view both go through `last_run_warning`, so they never disagree.
 */

export interface LastRun {
  id: string
  name: string
  start_date: string | null
  actual: number
}

export interface LastRunReference {
  median_actual: number
  runs: LastRun[]
}

/** A plan this many times what the event spent before is flagged… */
export const HIGH_RATIO = 1.5
/** …and so is one at or under this share of it: a plan missing most of what the event costs. */
export const LOW_RATIO = 0.5
/** …once the gap is at least this many dollars: $175 against $76 is not worth anyone's time. */
export const MIN_GAP_DOLLARS = 1_000

export interface LastRunWarning {
  /** Planned spend ÷ the reference, e.g. 1.91 — or 0 when nothing is planned. */
  ratio: number
  /** Which side of history the plan is on. */
  direction: "above" | "below"
  reference: LastRunReference
}

/**
 * Null when there is nothing to compare or the plan is in line with history. A plan well above
 * history is flagged (the over-projected volume Saffron hit on Whole Foods, 10/27), and so is one
 * well below it, including $0 planned for an event that has always cost money: Paul asked for both
 * on 10/29 (Saffron's Q4 UNFI MCB Culinary at $2,500 against $27,696; a Publix BOGO with nothing
 * loaded). An empty plan (`null`, the form not filled in yet) is never flagged.
 */
export function last_run_warning(planned: number | null | undefined, reference: LastRunReference | null | undefined): LastRunWarning | null {
  if (!reference || !(reference.median_actual > 0) || reference.runs.length === 0) return null
  if (planned == null || !(planned >= 0)) return null
  const ratio = planned / reference.median_actual
  const gap_cents = Math.round(planned * 100) - Math.round(reference.median_actual * 100)
  if (ratio >= HIGH_RATIO && gap_cents >= MIN_GAP_DOLLARS * 100) return { ratio, direction: "above", reference }
  if (ratio <= LOW_RATIO && -gap_cents >= MIN_GAP_DOLLARS * 100) return { ratio, direction: "below", reference }
  return null
}

/** "1.9×" — one decimal, trailing zero dropped ("2×"). */
export function format_ratio(ratio: number): string {
  return `${Number(ratio.toFixed(1))}×`
}

/** What the last runs were, for the warning's detail line: "April B … ($255,936), Feb B … ($220,807)". */
export function describe_runs(reference: LastRunReference, format_money: (amount: number) => string): string {
  return reference.runs.map((run: LastRun) => `${run.name} (${format_money(run.actual)})`).join(", ")
}

/** The sentence the forms show under Planned Spend. */
export function last_run_message(warning: LastRunWarning, format_money: (amount: number) => string): string {
  const runs = warning.reference.runs.length
  const basis = runs === 1 ? "the last time this promotion ran" : `the median of the last ${runs} times this promotion ran`
  const spent = format_money(warning.reference.median_actual)
  if (warning.direction === "below") {
    if (warning.ratio === 0) {
      return `Nothing is planned, but this promotion actually spent ${spent}, ${basis}. Load the plan so it is accrued, unless it is not running this time.`
    }
    return `This plan is ${format_ratio(warning.ratio)} what it actually spent: ${spent}, ${basis}. Check for missing volumes, stores or fees unless the deal changed.`
  }
  return `This plan is ${format_ratio(warning.ratio)} what it actually spent: ${spent}, ${basis}. Check the volumes and lift unless the deal, tactic or store count changed.`
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  function money(amount: number): string {
    return `$${Math.round(amount).toLocaleString("en-US")}`
  }

  // Saffron Road's Whole Foods off-shelf, 10/27: the last three settled runs and their median.
  const off_shelf: LastRunReference = {
    median_actual: 220_806.71,
    runs: [
      { id: "apr26", name: "April B - Off Shelf - Frozen Entrees - WFM 2026", start_date: "2026-04-08", actual: 255_936.04 },
      { id: "feb26", name: "Feb B Off Shelf - Frozen Entree - WFM 2026", start_date: "2026-02-11", actual: 220_806.71 },
      { id: "apr25", name: "April B Off Shelf - Frozen Entree - WFM 2025", start_date: "2025-04-09", actual: 166_902.88 },
    ],
  }

  describe("last_run_warning", () => {
    it("flags Saffron's October B off-shelf plan at 1.9× its last runs", () => {
      const warning = last_run_warning(422_494.28, off_shelf)
      expect(format_ratio(warning!.ratio)).toBe("1.9×")
    })

    it("flags August B too, at 1.6×", () => {
      expect(format_ratio(last_run_warning(364_114.04, off_shelf)!.ratio)).toBe("1.6×")
    })

    it("leaves a plan in line with history alone", () => {
      // September B TPR: $241,927 against a $186,307 median is 1.3×.
      expect(last_run_warning(241_927.33, { ...off_shelf, median_actual: 186_306.82 })).toBeNull()
      expect(last_run_warning(220_806.71 * 1.49, off_shelf)).toBeNull()
      expect(last_run_warning(220_806.71 * 0.51, off_shelf)).toBeNull()
    })

    it("flags a plan well below history, and $0 planned", () => {
      // Saffron's Q4 UNFI MCB Culinary: $2,500 planned against $40,330 (Q1) and $15,063 (Q2).
      const mcb = { median_actual: 27_696.22, runs: [off_shelf.runs[0], off_shelf.runs[1]] }
      const low = last_run_warning(2_500, mcb)!
      expect(low.direction).toBe("below")
      expect(format_ratio(low.ratio)).toBe("0.1×")
      expect(last_run_warning(0, mcb)!.direction).toBe("below")
      expect(last_run_warning(100_000, off_shelf)!.direction).toBe("below")
      expect(last_run_warning(422_494.28, off_shelf)!.direction).toBe("above")
    })

    it("leaves a plan a little below history alone", () => {
      expect(last_run_warning(220_806.71 * 0.51, off_shelf)).toBeNull()
      expect(last_run_warning(220_806.71 * 0.5, off_shelf)).not.toBeNull()
      // Under $1,000 short is not worth a flag: Town & Country's $76 event with nothing planned.
      expect(last_run_warning(0, { median_actual: 75.6, runs: [off_shelf.runs[0]] })).toBeNull()
    })

    it("ignores a gap under $1,000, however large the ratio", () => {
      // Saffron's Town & Country TPR: $175 planned against $76 spent.
      const small = { median_actual: 75.6, runs: [off_shelf.runs[0]] }
      expect(last_run_warning(175, small)).toBeNull()
      expect(last_run_warning(1_075.6, small)).not.toBeNull()
      expect(last_run_warning(1_075.59, small)).toBeNull()
    })

    it("says nothing without a plan or without history", () => {
      expect(last_run_warning(undefined, off_shelf)).toBeNull()
      expect(last_run_warning(null, off_shelf)).toBeNull()
      expect(last_run_warning(422_494.28, null)).toBeNull()
      expect(last_run_warning(422_494.28, { median_actual: 0, runs: [] })).toBeNull()
    })
  })

  describe("last_run_message", () => {
    it("names the basis and what to check", () => {
      const message = last_run_message(last_run_warning(422_494.28, off_shelf)!, money)
      expect(message).toBe(
        "This plan is 1.9× what it actually spent: $220,807, the median of the last 3 times this promotion ran. Check the volumes and lift unless the deal, tactic or store count changed.",
      )
    })

    it("says what to check on a plan below history", () => {
      const mcb = { median_actual: 27_696.22, runs: [off_shelf.runs[0], off_shelf.runs[1]] }
      expect(last_run_message(last_run_warning(2_500, mcb)!, money)).toBe(
        "This plan is 0.1× what it actually spent: $27,696, the median of the last 2 times this promotion ran. Check for missing volumes, stores or fees unless the deal changed.",
      )
      expect(last_run_message(last_run_warning(0, mcb)!, money)).toBe(
        "Nothing is planned, but this promotion actually spent $27,696, the median of the last 2 times this promotion ran. Load the plan so it is accrued, unless it is not running this time.",
      )
    })

    it("says 'the last time' for a single run", () => {
      const one = { median_actual: 100_000, runs: [off_shelf.runs[0]] }
      expect(last_run_message(last_run_warning(200_000, one)!, money)).toContain("$100,000, the last time this promotion ran")
    })

    it("describes the runs it compared against", () => {
      expect(describe_runs(off_shelf, money)).toBe(
        "April B - Off Shelf - Frozen Entrees - WFM 2026 ($255,936), Feb B Off Shelf - Frozen Entree - WFM 2026 ($220,807), April B Off Shelf - Frozen Entree - WFM 2025 ($166,903)",
      )
    })
  })

  describe("format_ratio", () => {
    it("drops a trailing zero", () => {
      expect(format_ratio(2.0)).toBe("2×")
      expect(format_ratio(0.45)).toBe("0.5×")
    })
  })
}
